import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton } from '../components/ui';
import { FiMail, FiPhone, FiArrowRight, FiArrowLeft, FiCheck } from 'react-icons/fi';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1717585679395-bbe39b5fb6bc?auto=format&fit=crop&w=900&q=80';

// mode: 'signin' | 'signup'
// method: 'phone' | 'email'
// phoneStep: 'number' | 'otp' | 'profile'   (profile = new-user name collection)

export default function LoginPage() {
  const [mode, setMode] = useState('signin');   // signin | signup
  const [method, setMethod] = useState('phone'); // phone | email
  const [phoneStep, setPhoneStep] = useState('number'); // number | otp | profile

  // phone OTP fields
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  // profile completion (new OTP users)
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  // email signin fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // email signup extra fields
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);

  const { sendOTP, verifyOTP, updateProfile, emailLogin, register, loading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  // ── helpers ──────────────────────────────────────────────────────────────────

  const switchMode = (m) => {
    setMode(m);
    setPhoneStep('number');
    setOtp(''); setPhone('');
    setEmail(''); setPassword('');
    setRegName(''); setRegPhone(''); setRegEmail(''); setRegPassword(''); setRegPasswordConfirm('');
  };

  const switchMethod = (m) => {
    setMethod(m);
    setPhoneStep('number');
    setOtp(''); setPhone('');
  };

  // ── phone OTP flow ────────────────────────────────────────────────────────────

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 10) { toast.error('Enter a valid 10-digit number'); return; }
    try {
      await sendOTP(phone.startsWith('+91') ? phone : `+91${phone}`);
      toast.success('OTP sent!');
      setPhoneStep('otp');
    } catch (err) { toast.error(err.message || 'Failed to send OTP'); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    try {
      const data = await verifyOTP(phone.startsWith('+91') ? phone : `+91${phone}`, otp);
      // New user (no name set yet) → go to profile step
      if (!data.user?.name) {
        setPhoneStep('profile');
        return;
      }
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) { toast.error(err.message || 'Invalid OTP'); }
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) { toast.error('Please enter your name'); return; }
    try {
      await updateProfile({ name: profileName.trim(), email: profileEmail.trim() || undefined });
      toast.success('Welcome to VritiEthnic!');
      navigate(from, { replace: true });
    } catch (err) { toast.error(err.message || 'Could not save profile'); }
  };

  // ── email sign-in ─────────────────────────────────────────────────────────────

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    try {
      const data = await emailLogin(email, password);
      toast.success('Welcome back!');
      navigate(data?.user?.role === 'admin' ? '/admin' : from, { replace: true });
    } catch (err) { toast.error(err.message || 'Login failed'); }
  };

  // ── email sign-up ─────────────────────────────────────────────────────────────

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim()) { toast.error('Enter your name'); return; }
    if (regPhone.length < 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (!regEmail) { toast.error('Enter your email'); return; }
    if (regPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (regPassword !== regPasswordConfirm) { toast.error('Passwords do not match'); return; }
    try {
      const phone10 = regPhone.startsWith('+91') ? regPhone : `+91${regPhone}`;
      await register(regName.trim(), phone10, regEmail.trim(), regPassword);
      toast.success('Account created! Welcome to VritiEthnic 🎉');
      navigate(from, { replace: true });
    } catch (err) { toast.error(err.message || 'Registration failed'); }
  };

  // ── left-panel copy adapts to mode ───────────────────────────────────────────

  const heroCopy = mode === 'signin'
    ? { chip: 'वृति का त्यौहार', headline: <>Welcome back to <em style={{ background: 'linear-gradient(135deg,#E9C77A,#B08A3E 60%,#7A5618)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic' }}>your edit.</em></>, body: 'Sign in to access your wishlist, order history, and member tier discounts.' }
    : { chip: 'Join VritiEthnic', headline: <>Start your <em style={{ background: 'linear-gradient(135deg,#E9C77A,#B08A3E 60%,#7A5618)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic' }}>style story.</em></>, body: 'Create a free account to shop handcrafted kurtis, save wishlists, and unlock Saheli & Sakhi member perks.' };

  return (
    <div style={{ minHeight: 'calc(100vh - 120px)', display: 'grid', gridTemplateColumns: '1.05fr 1fr' }} className="login-grid">
      {/* ── Left: Aurora panel ── */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '64px 56px' }} className="login-hero">
        <img src={HERO_IMAGE} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.32 }} />
        <AuroraBlobs />
        <GrainOverlay />
        <AnimatePresence mode="wait">
          <motion.div key={mode} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} style={{ position: 'relative', zIndex: 2, maxWidth: 480 }}>
            <GlassChip>{heroCopy.chip}</GlassChip>
            <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.2vw, 60px)', lineHeight: 1.08, color: '#FFF', marginTop: 22, marginBottom: 18, letterSpacing: '-0.02em' }}>
              {heroCopy.headline}
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(237,227,209,0.72)', marginBottom: 28 }}>{heroCopy.body}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingTop: 22, borderTop: '1px solid rgba(237,227,209,0.14)' }}>
              <Stat n="50K+" l="Members" />
              <span style={{ width: 1, height: 28, background: 'rgba(237,227,209,0.15)' }} />
              <Stat n="4.8★" l="Trusted" />
              <span style={{ width: 1, height: 28, background: 'rgba(237,227,209,0.15)' }} />
              <Stat n="120+" l="Artisans" />
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* ── Right: Form panel ── */}
      <section style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', background: 'var(--bg)', overflowY: 'auto' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} style={{ width: '100%', maxWidth: 420 }}>

          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span className="serif" style={{ fontSize: 34, color: 'var(--primary)', letterSpacing: '-0.01em' }}>VritiEthnic</span>
              <span className="hindi" style={{ fontSize: 13, color: 'var(--muted)' }}>वृति</span>
            </Link>
          </div>

          {/* Sign In / Create Account toggle */}
          <div style={{ display: 'flex', background: 'rgba(42,31,26,0.06)', borderRadius: 999, padding: 4, gap: 4, marginBottom: 24 }}>
            {[{ id: 'signin', label: 'Sign in' }, { id: 'signup', label: 'Create account' }].map(m => (
              <button key={m.id} onClick={() => switchMode(m.id)} style={{
                flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer', borderRadius: 999,
                fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'inherit',
                background: mode === m.id ? '#FFF' : 'transparent',
                color: mode === m.id ? 'var(--ink)' : 'var(--muted)',
                boxShadow: mode === m.id ? '0 2px 8px rgba(42,31,26,0.1)' : 'none',
                transition: 'all 0.2s ease',
              }}>
                {m.label}
              </button>
            ))}
          </div>

          <div className="ve-glass-form-card">
            {/* Phone / Email method tabs */}
            <div style={{ display: 'flex', padding: '6px 6px 0', gap: 4, background: 'rgba(255,255,255,0.4)' }}>
              {[{ id: 'phone', label: 'Phone OTP', icon: <FiPhone size={13} /> }, { id: 'email', label: 'Email', icon: <FiMail size={13} /> }].map(t => (
                <button key={t.id} onClick={() => switchMethod(t.id)} style={{
                  flex: 1, padding: '11px 0', borderBottom: method === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                  border: 'none', borderRadius: '10px 10px 0 0', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: method === t.id ? 'var(--primary)' : 'var(--muted)',
                  background: method === t.id ? 'rgba(122,31,43,0.05)' : 'transparent',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  transition: 'all 0.18s ease',
                }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            <div style={{ padding: '28px 28px 24px' }}>
              <AnimatePresence mode="wait">
                {/* ─── Phone OTP ─── */}
                {method === 'phone' && phoneStep === 'number' && (
                  <motion.div key="phone-number" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.22 }}>
                    <h2 className="serif" style={{ fontSize: 24, textAlign: 'center', marginBottom: 4 }}>
                      {mode === 'signup' ? 'Create account' : 'Sign in'}
                    </h2>
                    <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
                      {mode === 'signup'
                        ? "We'll create your account with a one-time SMS code."
                        : "We'll text a one-time code to your number."}
                    </p>
                    <form onSubmit={handleSendOTP}>
                      <label className="fld-lab">Mobile number</label>
                      <PhoneInput value={phone} onChange={setPhone} />
                      <MagneticButton variant="primary" size="lg" type="submit" disabled={loading || phone.length < 10} style={{ width: '100%', marginTop: 20 }}>
                        {loading ? 'Sending…' : <>Send OTP <FiArrowRight size={14} /></>}
                      </MagneticButton>
                    </form>
                  </motion.div>
                )}

                {method === 'phone' && phoneStep === 'otp' && (
                  <motion.div key="phone-otp" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}>
                    <h2 className="serif" style={{ fontSize: 24, textAlign: 'center', marginBottom: 4 }}>Enter OTP</h2>
                    <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
                      Sent to <strong style={{ color: 'var(--ink)' }}>+91 {phone}</strong>
                    </p>
                    <form onSubmit={handleVerifyOTP}>
                      <label className="fld-lab">6-digit OTP</label>
                      <input
                        type="text" inputMode="numeric" autoFocus required
                        value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="••••••"
                        className="ve-fld"
                        style={{ textAlign: 'center', fontSize: 28, letterSpacing: '0.5em', fontWeight: 700, height: 58 }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 8, marginBottom: 20 }}>Valid for 10 minutes</div>
                      <MagneticButton variant="primary" size="lg" type="submit" disabled={loading || otp.length !== 6} style={{ width: '100%' }}>
                        {loading ? 'Verifying…' : <>Verify & continue <FiArrowRight size={14} /></>}
                      </MagneticButton>
                      <button type="button" onClick={() => { setPhoneStep('number'); setOtp(''); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center', marginTop: 12, background: 'transparent', border: 'none', fontSize: 12, color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', padding: '8px 0', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
                        <FiArrowLeft size={12} /> Change number
                      </button>
                    </form>
                  </motion.div>
                )}

                {method === 'phone' && phoneStep === 'profile' && (
                  <motion.div key="phone-profile" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                      <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),var(--gold))', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: '0 8px 24px rgba(122,31,43,0.25)' }}>
                        <FiCheck size={26} color="#FFF" />
                      </div>
                      <h2 className="serif" style={{ fontSize: 24, marginBottom: 4 }}>One last step</h2>
                      <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>Tell us your name to complete your account.</p>
                    </div>
                    <form onSubmit={handleCompleteProfile}>
                      <div style={{ marginBottom: 16 }}>
                        <label className="fld-lab">Your name *</label>
                        <input autoFocus required className="ve-fld" value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="Priya Sharma" />
                      </div>
                      <div style={{ marginBottom: 22 }}>
                        <label className="fld-lab">Email <span style={{ color: 'var(--muted)', fontWeight: 400, letterSpacing: 0 }}>(optional)</span></label>
                        <input type="email" className="ve-fld" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} placeholder="priya@example.com" />
                      </div>
                      <MagneticButton variant="primary" size="lg" type="submit" disabled={loading} style={{ width: '100%' }}>
                        {loading ? 'Saving…' : <>Complete account <FiArrowRight size={14} /></>}
                      </MagneticButton>
                    </form>
                  </motion.div>
                )}

                {/* ─── Email — Sign in ─── */}
                {method === 'email' && mode === 'signin' && (
                  <motion.div key="email-signin" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}>
                    <h2 className="serif" style={{ fontSize: 24, textAlign: 'center', marginBottom: 4 }}>Sign in</h2>
                    <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginBottom: 24 }}>Email and password</p>
                    <form onSubmit={handleEmailLogin}>
                      <div style={{ marginBottom: 16 }}>
                        <label className="fld-lab">Email address</label>
                        <input type="email" className="ve-fld" autoFocus required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                      </div>
                      <div style={{ marginBottom: 22 }}>
                        <label className="fld-lab">Password</label>
                        <PasswordInput value={password} onChange={setPassword} show={showPw} onToggle={() => setShowPw(v => !v)} placeholder="••••••••" />
                      </div>
                      <MagneticButton variant="primary" size="lg" type="submit" disabled={loading} style={{ width: '100%' }}>
                        {loading ? 'Signing in…' : <>Sign in <FiArrowRight size={14} /></>}
                      </MagneticButton>
                    </form>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <button onClick={() => switchMode('signup')} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
                        Don't have an account? Create one
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ─── Email — Create account ─── */}
                {method === 'email' && mode === 'signup' && (
                  <motion.div key="email-signup" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}>
                    <h2 className="serif" style={{ fontSize: 24, textAlign: 'center', marginBottom: 4 }}>Create account</h2>
                    <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginBottom: 22 }}>Fill in your details to get started</p>
                    <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <label className="fld-lab">Full name *</label>
                        <input autoFocus required className="ve-fld" value={regName} onChange={e => setRegName(e.target.value)} placeholder="Priya Sharma" />
                      </div>
                      <div>
                        <label className="fld-lab">Mobile number *</label>
                        <PhoneInput value={regPhone} onChange={setRegPhone} />
                      </div>
                      <div>
                        <label className="fld-lab">Email address *</label>
                        <input type="email" required className="ve-fld" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="you@example.com" />
                      </div>
                      <div>
                        <label className="fld-lab">Password * <span style={{ color: 'var(--muted)', fontWeight: 400, letterSpacing: 0 }}>min 8 characters</span></label>
                        <PasswordInput value={regPassword} onChange={setRegPassword} show={showPw} onToggle={() => setShowPw(v => !v)} placeholder="Create a password" />
                      </div>
                      <div>
                        <label className="fld-lab">Confirm password *</label>
                        <input type="password" required className="ve-fld" value={regPasswordConfirm} onChange={e => setRegPasswordConfirm(e.target.value)} placeholder="Repeat password" />
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <MagneticButton variant="primary" size="lg" type="submit" disabled={loading} style={{ width: '100%' }}>
                          {loading ? 'Creating account…' : <>Create account <FiArrowRight size={14} /></>}
                        </MagneticButton>
                      </div>
                    </form>
                    <div style={{ textAlign: 'center', marginTop: 14 }}>
                      <button onClick={() => switchMode('signin')} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
                        Already have an account? Sign in
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Terms */}
              <p style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
                By continuing, you agree to our{' '}
                <Link to="/terms" style={{ color: 'var(--primary)' }}>Terms</Link>{' '}and{' '}
                <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>.
              </p>
            </div>
          </div>

          {/* Admin link */}
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/admin/login" style={{ fontSize: 11, color: 'var(--muted)', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Admin login ›</Link>
          </div>
        </motion.div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .login-grid { grid-template-columns: 1fr !important; }
          .login-hero { min-height: 300px; padding: 40px 32px !important; }
        }
      `}</style>
    </div>
  );
}

// ──────────────── Sub-components ────────────────

function PhoneInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
      <span style={{ padding: '0 14px', background: 'rgba(42,31,26,0.05)', display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 700, borderRight: '1px solid var(--line)', flexShrink: 0, color: 'var(--ink)' }}>+91</span>
      <input
        type="tel" autoComplete="tel" required
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder="98XXXXXXXX"
        style={{ flex: 1, height: 48, padding: '0 14px', border: 'none', outline: 'none', fontSize: 15, background: 'transparent', fontFamily: 'inherit', color: 'var(--ink)' }}
      />
    </div>
  );
}

function PasswordInput({ value, onChange, show, onToggle, placeholder }) {
  return (
    <div style={{ position: 'relative', marginTop: 6 }}>
      <input
        type={show ? 'text' : 'password'} required
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="ve-fld"
        style={{ paddingRight: 44 }}
      />
      <button type="button" onClick={onToggle} style={{
        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--muted)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', fontFamily: 'inherit',
      }}>
        {show ? 'HIDE' : 'SHOW'}
      </button>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="serif" style={{ fontSize: 20, color: '#FFF', lineHeight: 1, marginBottom: 2 }}>{n}</div>
      <div style={{ fontSize: 10, color: 'rgba(237,227,209,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{l}</div>
    </div>
  );
}
